import axios from 'axios'
import rules from '../../../proxyConfig/rules'
import { rewritePath } from '../../utils/pathRewriter'
import { useMtlsCertificateStore } from '../../zustand/mtls-certificate/use-mtls-certificate-store.jsx'
import { axiosFingerprint, reportError } from '../../utils/report-error'

let authStorePromise = null

const getAuthStore = async () => {
  if (!authStorePromise) {
    authStorePromise =
      import('../../zustand/authentication/use-auth-store').then(
        ({ useAuthStore }) => useAuthStore
      )
  }

  return authStorePromise
}

const api = axios.create({
  baseURL: rules.target,
  timeout: 30000,
  headers: {
    accept: 'application/json, text/plain, */*',
    'X-Platform': 'WEB'
  }
})

api.CancelToken = axios.CancelToken
api.isCancel = axios.isCancel

// Interceptor to include dynamic headers
api.interceptors.request.use(async (config) => {
  // A retried config comes back through here with its url already rewritten,
  // and rewritePath isn't idempotent, so always rewrite from the caller's path
  config.__originalUrl = config.__originalUrl ?? config.url

  const { path, baseURL } = rewritePath({
    path: config.__originalUrl,
    baseURL: self.location.origin
  })

  config.baseURL = baseURL
  config.url = path

  // if the request is marked with skipHeaders, clean all headers
  if (config.skipHeaders) {
    config.headers = {}
    return config
  }

  // Verify if header Authorization already exists
  const hasCustomAuthorization = config.headers && config.headers.Authorization

  // If no custom Authorization header exists, add the one from the auth store
  if (!hasCustomAuthorization) {
    const token = (await getAuthStore()).getState().token
    if (token && config.headers) {
      config.headers.Authorization = `${token.token_type} ${token.access_token}`
    }
  }
  return config
})

// Interceptor to detect expired token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      if (
        error.response.data &&
        error.response.data.error === 'invalid_token' &&
        error.response.data.error_description &&
        error.response.data.error_description.includes('Access token expired:')
      ) {
        const authStore = await getAuthStore()
        authStore.getState().setExpiredToken(true)
      }
    }
    return Promise.reject(error)
  }
)

// Check if the error is related to certificate issues - MTLS Handlers
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const state = useMtlsCertificateStore.getState()

    if (state.isMtlsRequired) {
      if (
        state.mtlsEnabled &&
        error?.config?.baseURL?.includes(state.endpoint)
      ) {
        const setIsMtlsRequired = state.setIsMtlsRequired

        setIsMtlsRequired(true)
      }
    }

    return Promise.reject(error)
  }
)

const NETWORK_ERROR_RETRY_LIMIT = 2
const NETWORK_ERROR_RETRY_DELAY_MS = 500

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// Retries GET requests that fail with a genuine network error (connection
// dropped, DNS blip, offline moment) before they ever reach reportError below.
// Scoped to error.code === 'ERR_NETWORK' only - never 4xx/5xx or timeouts,
// and never non-GET methods, since retrying a request whose response we
// lost (not the request itself) could duplicate a write.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const isNetworkError = error?.code === 'ERR_NETWORK'
    const isGetRequest = error?.config?.method?.toLowerCase() === 'get'

    if (!isNetworkError || !isGetRequest || axios.isCancel(error)) {
      return Promise.reject(error)
    }

    const config = error.config
    config.__networkErrorRetryCount = (config.__networkErrorRetryCount || 0) + 1

    if (config.__networkErrorRetryCount > NETWORK_ERROR_RETRY_LIMIT) {
      return Promise.reject(error)
    }

    await wait(NETWORK_ERROR_RETRY_DELAY_MS * config.__networkErrorRetryCount)
    return api(config)
  }
)

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    const isCanceled = axios.isCancel(error)

    const mtlsState = useMtlsCertificateStore.getState()
    const isMtlsScenario =
      mtlsState.mtlsEnabled &&
      !!mtlsState.endpoint &&
      error?.config?.baseURL?.includes(mtlsState.endpoint)

    if (!isCanceled && !isMtlsScenario) {
      reportError(error, {
        tags: {
          'http.status': status ?? 'network_error',
          'http.method': error?.config?.method?.toUpperCase() ?? 'unknown'
        },
        extra: {
          url: error?.config?.url,
          baseURL: error?.config?.baseURL,
          data: error?.response?.data
        },
        fingerprint: axiosFingerprint(error, status)
      })
    }

    return Promise.reject(error)
  }
)

export default api
